import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";import {
  ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger,
  ContextMenuSeparator, ContextMenuSub, ContextMenuSubTrigger, ContextMenuSubContent,
} from "@/components/ui/context-menu";
import {
  Plus, Building2, Layers, DoorOpen, User, X, MoreHorizontal, Pencil,
  Trash2, ArrowRightLeft, Sparkles, Search, ChevronRight, ChevronDown,
  ShieldAlert, LogOut, UserPlus, RefreshCw, Loader2, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors,
  DragOverlay, closestCenter,
} from "@dnd-kit/core";

/* =========================================================
 *  Utility helpers
 * =======================================================*/
const STATUS_COLORS = {
  available: "bg-emerald-50 border-emerald-300 text-emerald-800",
  occupied:  "bg-primary/10 border-primary/50 text-primary",
  reserved:  "bg-amber-50 border-amber-300 text-amber-800",
  maintenance: "bg-slate-100 border-slate-300 text-slate-600",
  blocked:   "bg-rose-50 border-rose-300 text-rose-700",
};
const STATUS_DOT = {
  available: "bg-emerald-500",
  occupied:  "bg-primary",
  reserved:  "bg-amber-500",
  maintenance: "bg-slate-400",
  blocked:   "bg-rose-500",
};

const roomStats = (bedsHere) => {
  const total = bedsHere.length;
  const occupied = bedsHere.filter((b) => b.status === "occupied").length;
  const reserved = bedsHere.filter((b) => b.status === "reserved").length;
  const maintenance = bedsHere.filter((b) => b.status === "maintenance").length;
  const blocked = bedsHere.filter((b) => b.status === "blocked").length;
  const vacant = bedsHere.filter((b) => b.status === "available").length;
  const pct = total ? Math.round((occupied / total) * 100) : 0;
  return { total, occupied, reserved, maintenance, blocked, vacant, pct };
};

/* =========================================================
 *  Draggable bed / room wrappers (dnd-kit)
 * =======================================================*/
function DraggableBed({ bed, canDrag, children }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `bed:${bed.id}`, data: { type: "bed", bed }, disabled: !canDrag,
  });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
      className={isDragging ? "opacity-40" : ""}>
      {children}
    </div>
  );
}

function DroppableRoom({ roomId, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `room:${roomId}`, data: { type: "room", roomId },
  });
  return (
    <div ref={setNodeRef}
      className={`rounded-lg transition-shadow ${isOver ? "ring-2 ring-primary ring-offset-2" : ""}`}>
      {children}
    </div>
  );
}

function DraggableRoomCard({ roomId, canDrag, children }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `roomcard:${roomId}`, data: { type: "roomcard", roomId }, disabled: !canDrag,
  });
  return (
    <div ref={setNodeRef} className={isDragging ? "opacity-40" : ""}>
      {/* Grab handle */}
      <div {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing">
        {children}
      </div>
    </div>
  );
}

function DroppableFloor({ floorId, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `floor:${floorId}`, data: { type: "floor", floorId },
  });
  return (
    <div ref={setNodeRef}
      className={`rounded-lg transition-all ${isOver ? "ring-2 ring-primary bg-primary/5" : ""}`}>
      {children}
    </div>
  );
}

/* =========================================================
 *  Main Page
 * =======================================================*/
export default function BedAllocation() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "warden" || user?.role === "hostel_manager";

  const [loading, setLoading] = useState(true);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);   // all floors across buildings
  const [rooms, setRooms] = useState([]);     // all rooms across floors
  const [beds, setBeds] = useState([]);       // all beds
  const [residents, setResidents] = useState([]);

  const [bId, setBId] = useState("");         // selected building
  const [fId, setFId] = useState("");         // selected floor ("all" = all floors of the building)
  const [expanded, setExpanded] = useState({});  // tree collapse state per building id

  // Filters
  const [search, setSearch] = useState("");
  const [bedStatusFilter, setBedStatusFilter] = useState("all");
  const [occupancyFilter, setOccupancyFilter] = useState("all");

  // Dialogs
  const [addBuildingOpen, setAddBuildingOpen] = useState(false);
  const [addFloorOpen, setAddFloorOpen] = useState(false);
  const [addRoomOpen, setAddRoomOpen] = useState(false);
  const [editBuilding, setEditBuilding] = useState(null);
  const [editFloor, setEditFloor] = useState(null);
  const [editRoom, setEditRoom] = useState(null);
  const [editBed, setEditBed] = useState(null);
  const [generateFor, setGenerateFor] = useState(null);   // room to bulk generate beds in
  const [assignBed, setAssignBed] = useState(null);
  const [assignQuery, setAssignQuery] = useState("");
  const [assignResults, setAssignResults] = useState([]);
  const [confirm, setConfirm] = useState(null);           // { title, description, onConfirm }
  const [viewResidentsOf, setViewResidentsOf] = useState(null); // room
  const [setFeeRoom, setSetFeeRoom] = useState(null);           // v34 — Set Monthly Fee dialog

  // DnD overlay content
  const [dragging, setDragging] = useState(null);         // {type: 'bed'|'roomcard', id, label}

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  /* ---------- data loading ---------- */
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, all_f, all_r, all_beds, res] = await Promise.all([
        api.get("/buildings"),
        api.get("/floors"),
        api.get("/rooms"),
        api.get("/beds"),
        api.get("/residents"),
      ]);
      setBuildings(b.data);
      setFloors(all_f.data);
      setRooms(all_r.data);
      setBeds(all_beds.data);
      setResidents(res.data);
      if (!bId && b.data.length) {
        setBId(b.data[0].id);
        setExpanded((prev) => ({ ...prev, [b.data[0].id]: true }));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [bId]);

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  /* ---------- search residents dialog ---------- */
  useEffect(() => {
    if (!assignQuery.trim()) { setAssignResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/students/search?q=${encodeURIComponent(assignQuery)}`)
        .then((r) => setAssignResults(r.data))
        .catch(() => setAssignResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [assignQuery]);

  /* ---------- derived data ---------- */
  const floorsInBuilding = useMemo(
    () => floors.filter((f) => f.building_id === bId).sort((a, b) => a.floor_number - b.floor_number),
    [floors, bId],
  );
  const roomsInScope = useMemo(() => {
    if (!bId) return [];
    if (fId && fId !== "all") return rooms.filter((r) => r.floor_id === fId);
    const fIds = floorsInBuilding.map((f) => f.id);
    return rooms.filter((r) => fIds.includes(r.floor_id));
  }, [rooms, bId, fId, floorsInBuilding]);

  const bedsByRoom = useMemo(() => {
    const m = {};
    for (const b of beds) { (m[b.room_id] ||= []).push(b); }
    for (const k in m) m[k].sort((a,b) => a.bed_no.localeCompare(b.bed_no, undefined, { numeric: true }));
    return m;
  }, [beds]);

  const residentById = useMemo(() => {
    const m = {}; for (const r of residents) m[r.id] = r; return m;
  }, [residents]);

  const searchLower = search.trim().toLowerCase();
  const filteredRooms = useMemo(() => {
    let list = roomsInScope;
    if (occupancyFilter !== "all") {
      list = list.filter((r) => {
        const st = roomStats(bedsByRoom[r.id] || []);
        if (occupancyFilter === "full")    return st.total > 0 && st.occupied === st.total;
        if (occupancyFilter === "partial") return st.occupied > 0 && st.occupied < st.total;
        if (occupancyFilter === "empty")   return st.occupied === 0;
        return true;
      });
    }
    if (searchLower) {
      list = list.filter((r) => {
        if (r.room_no?.toLowerCase().includes(searchLower)) return true;
        const bh = bedsByRoom[r.id] || [];
        for (const b of bh) {
          if (b.bed_no?.toLowerCase().includes(searchLower)) return true;
          const res = residentById[b.resident_id];
          if (res && res.name?.toLowerCase().includes(searchLower)) return true;
        }
        return false;
      });
    }
    return list;
  }, [roomsInScope, bedsByRoom, occupancyFilter, searchLower, residentById]);

  /* ---------- helpers ---------- */
  const currentBuilding = buildings.find((b) => b.id === bId);
  const floorName = (id) => floors.find((f) => f.id === id)?.name || "—";
  const askConfirm = (opts) => setConfirm(opts);

  /* ---------- API actions ---------- */
  const doDeleteBed  = async (bed) => {
    try { await api.delete(`/beds/${bed.id}`); toast.success(`Bed ${bed.bed_no} deleted`); loadAll(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const doDeleteRoom = async (room) => {
    try { await api.delete(`/rooms/${room.id}`); toast.success(`Room ${room.room_no} deleted`); loadAll(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const doDeleteFloor = async (floor) => {
    try { await api.delete(`/floors/${floor.id}`); toast.success(`Floor ${floor.name} deleted`); loadAll(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const doDeleteBuilding = async (b) => {
    try { await api.delete(`/buildings/${b.id}`); toast.success(`Building ${b.name} deleted`);
      if (bId === b.id) { setBId(""); setFId(""); }
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const doVacate = async (bed) => {
    try { await api.post(`/beds/${bed.id}/vacate`); toast.success("Bed vacated"); loadAll(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const doSetBedStatus = async (bed, status) => {
    try { await api.put(`/beds/${bed.id}/status`, { status });
      toast.success(`Bed marked ${status}`); loadAll(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const doAssign = async (resident) => {
    try {
      await api.post(`/beds/${assignBed.id}/assign`, { resident_id: resident.id });
      toast.success(`Assigned ${resident.name} to ${assignBed.bed_no}`);
      setAssignBed(null); setAssignQuery(""); setAssignResults([]);
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  /* ---------- Dnd handlers ---------- */
  const onDragStart = (e) => {
    const d = e.active.data.current;
    if (d?.type === "bed")     setDragging({ type: "bed", label: d.bed.bed_no });
    if (d?.type === "roomcard") {
      const r = rooms.find((x) => x.id === d.roomId);
      setDragging({ type: "room", label: r ? `Room ${r.room_no}` : "" });
    }
  };
  const onDragEnd = async (e) => {
    setDragging(null);
    const active = e.active?.data?.current;
    const over = e.over?.data?.current;
    if (!active || !over) return;
    try {
      if (active.type === "bed" && over.type === "room") {
        if (active.bed.room_id === over.roomId) return;
        const targetRoom = rooms.find((r) => r.id === over.roomId);
        const suggested = `${targetRoom.room_no}-${(active.bed.bed_no.split("-").pop() || "1")}`;
        await api.post(`/beds/${active.bed.id}/transfer`, {
          target_room_id: over.roomId, new_bed_no: suggested,
        });
        toast.success(`Bed moved to ${targetRoom.room_no}`);
        loadAll();
      } else if (active.type === "roomcard" && over.type === "floor") {
        if (active.roomId === over.floorId) return;
        const room = rooms.find((r) => r.id === active.roomId);
        if (!room) return;
        if (room.floor_id === over.floorId) return;
        await api.put(`/rooms/${active.roomId}`, { floor_id: over.floorId });
        toast.success("Room moved to new floor");
        loadAll();
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  /* =========================================================
   *  RENDER
   * =======================================================*/
  return (
    <div className="space-y-6" data-testid="beds-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Layout</div>
          <h1 className="font-display text-4xl font-bold mt-1">Bed Allocation</h1>
          <p className="text-muted-foreground mt-1">
            Building → Floor → Room → Bed. Drag rooms across floors and beds across rooms.
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => loadAll()} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button variant="outline" onClick={() => setAddBuildingOpen(true)} data-testid="add-building-btn">
              <Building2 className="w-4 h-4 mr-2" /> Building
            </Button>
            <Button variant="outline" disabled={!bId} onClick={() => setAddFloorOpen(true)} data-testid="add-floor-btn">
              <Layers className="w-4 h-4 mr-2" /> Floor
            </Button>
            <Button disabled={!fId || fId === "all"} onClick={() => setAddRoomOpen(true)} data-testid="add-room-btn">
              <Plus className="w-4 h-4 mr-2" /> Room
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <Card className="p-10 border-border bg-white text-center text-muted-foreground">
          <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> Loading…
        </Card>
      ) : buildings.length === 0 ? (
        <Card className="p-10 border-border bg-white text-center text-muted-foreground">
          No buildings yet. {canEdit && "Click Building to start."}
        </Card>
      ) : (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}
                    collisionDetection={closestCenter}>
          <div className="grid grid-cols-12 gap-6">
            {/* ============= LEFT: TREE ============= */}
            <aside className="col-span-12 lg:col-span-3">
              <Card className="p-3 border-border bg-white sticky top-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1">
                  Facilities
                </div>
                <div className="space-y-1 mt-1 max-h-[70vh] overflow-y-auto">
                  {buildings.map((b) => {
                    const bFloors = floors.filter((f) => f.building_id === b.id)
                                         .sort((a,b) => a.floor_number - b.floor_number);
                    const isOpen = !!expanded[b.id];
                    const isActive = bId === b.id;
                    return (
                      <div key={b.id}>
                        <div className={`group flex items-center rounded-md ${isActive ? "bg-primary/10" : "hover:bg-secondary"}`}>
                          <button onClick={() => setExpanded((e)=>({...e, [b.id]: !isOpen}))}
                            className="p-1.5 text-muted-foreground">
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            onClick={() => { setBId(b.id); setFId("all"); setExpanded((e)=>({...e,[b.id]:true})); }}
                            className="flex-1 flex items-center gap-2 py-1.5 text-left text-sm"
                            data-testid={`tree-building-${b.id}`}>
                            <Building2 className="w-4 h-4 text-primary" />
                            <span className="truncate">{b.name}</span>
                          </button>
                          {canEdit && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1 opacity-60 hover:opacity-100 mr-1" data-testid={`bldg-menu-${b.id}`}>
                                  <MoreHorizontal className="w-3.5 h-3.5" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setEditBuilding(b)}>
                                  <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => askConfirm({
                                    title: `Delete building ${b.name}?`,
                                    description: "This is only allowed when the building has no floors.",
                                    onConfirm: () => doDeleteBuilding(b),
                                    variant: "destructive",
                                  })} className="text-destructive">
                                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                        {isOpen && (
                          <div className="ml-6 space-y-0.5 border-l border-border pl-2">
                            <button onClick={() => { setBId(b.id); setFId("all"); }}
                              className={`w-full text-left text-xs px-2 py-1 rounded-md ${
                                bId===b.id && fId==="all" ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-secondary"
                              }`}>
                              All floors ({bFloors.length})
                            </button>
                            {bFloors.map((f) => (
                              <DroppableFloor key={f.id} floorId={f.id}>
                                <div className={`flex items-center rounded-md ${bId===b.id && fId===f.id ? "bg-primary/10" : "hover:bg-secondary"}`}>
                                  <button onClick={() => { setBId(b.id); setFId(f.id); }}
                                    className="flex-1 flex items-center gap-2 text-xs px-2 py-1.5 text-left"
                                    data-testid={`tree-floor-${f.id}`}>
                                    <Layers className="w-3 h-3 text-muted-foreground" />
                                    <span className="truncate">{f.name} · F{f.floor_number}</span>
                                  </button>
                                  {canEdit && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <button className="p-1 opacity-60 hover:opacity-100 mr-1"
                                          data-testid={`floor-menu-${f.id}`}>
                                          <MoreHorizontal className="w-3 h-3" />
                                        </button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => setEditFloor(f)}>
                                          <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => askConfirm({
                                          title: `Delete floor ${f.name}?`,
                                          description: "Only allowed when the floor has no rooms.",
                                          onConfirm: () => doDeleteFloor(f),
                                          variant: "destructive",
                                        })} className="text-destructive">
                                          <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </div>
                              </DroppableFloor>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            </aside>

            {/* ============= RIGHT: ROOM WALL ============= */}
            <section className="col-span-12 lg:col-span-9 space-y-4">
              {/* Filters + summary */}
              <Card className="p-4 border-border bg-white">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[220px] relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search rooms, beds or residents…" className="pl-9"
                      value={search} onChange={(e) => setSearch(e.target.value)}
                      data-testid="beds-search" />
                  </div>
                  <div className="min-w-[160px]">
                    <Select value={bedStatusFilter} onValueChange={setBedStatusFilter}>
                      <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Bed status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="occupied">Occupied</SelectItem>
                        <SelectItem value="reserved">Reserved</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="blocked">Blocked</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-[160px]">
                    <Select value={occupancyFilter} onValueChange={setOccupancyFilter}>
                      <SelectTrigger data-testid="filter-occupancy"><SelectValue placeholder="Occupancy" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All rooms</SelectItem>
                        <SelectItem value="empty">Empty</SelectItem>
                        <SelectItem value="partial">Partially occupied</SelectItem>
                        <SelectItem value="full">Full</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {currentBuilding && (
                  <div className="mt-3 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{currentBuilding.name}</span> ·{" "}
                    {floorsInBuilding.length} floor(s) · {roomsInScope.length} room(s) ·{" "}
                    {roomsInScope.reduce((n, r) => n + (bedsByRoom[r.id]?.length || 0), 0)} beds
                  </div>
                )}
              </Card>

              {/* Room grid */}
              {filteredRooms.length === 0 ? (
                <Card className="p-8 border-border bg-white text-center text-muted-foreground">
                  {roomsInScope.length === 0
                    ? (fId ? "No rooms on this floor yet." : "Select a floor or add a new one.")
                    : "No rooms match your filter."}
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredRooms.map((room) => (
                    <RoomCard
                      key={room.id}
                      room={room}
                      floorName={floorName(room.floor_id)}
                      beds={bedsByRoom[room.id] || []}
                      residentById={residentById}
                      bedStatusFilter={bedStatusFilter}
                      canEdit={canEdit}
                      searchHighlight={searchLower}
                      onEditRoom={() => setEditRoom(room)}
                      onDeleteRoom={() => askConfirm({
                        title: `Delete room ${room.room_no}?`,
                        description: "All vacant beds in this room will also be removed. Not allowed while any bed is occupied.",
                        onConfirm: () => doDeleteRoom(room),
                        variant: "destructive",
                      })}
                      onTransferRoom={() => setEditRoom({ ...room, __openTransfer: true })}
                      onGenerateBeds={() => setGenerateFor(room)}
                      onViewResidents={() => setViewResidentsOf(room)}
                      onSetFee={() => setSetFeeRoom(room)}
                      onEditBed={(bed) => setEditBed(bed)}
                      onDeleteBed={(bed) => askConfirm({
                        title: `Delete bed ${bed.bed_no}?`,
                        description: "Only allowed when the bed is vacant.",
                        onConfirm: () => doDeleteBed(bed),
                        variant: "destructive",
                      })}
                      onAssign={(bed) => setAssignBed(bed)}
                      onVacate={(bed) => askConfirm({
                        title: `Vacate ${bed.bed_no}?`,
                        description: `This will remove ${residentById[bed.resident_id]?.name || "the resident"} from this bed.`,
                        onConfirm: () => doVacate(bed),
                      })}
                      onStatus={doSetBedStatus}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>

          <DragOverlay dropAnimation={null}>
            {dragging ? (
              <div className="rounded-md border border-primary bg-white shadow-lg px-3 py-2 text-xs font-medium">
                Moving: {dragging.label}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* ============= DIALOGS ============= */}
      <AddBuildingDialog open={addBuildingOpen} onOpenChange={setAddBuildingOpen} onDone={loadAll} setBId={setBId} />
      <AddFloorDialog open={addFloorOpen} onOpenChange={setAddFloorOpen} bId={bId} floors={floorsInBuilding} onDone={loadAll} />
      <AddRoomDialog open={addRoomOpen} onOpenChange={setAddRoomOpen} fId={fId} onDone={loadAll} />

      <EditBuildingDialog building={editBuilding} onClose={() => setEditBuilding(null)} onDone={loadAll} />
      <EditFloorDialog floor={editFloor} onClose={() => setEditFloor(null)} onDone={loadAll} />
      <EditRoomDialog room={editRoom} floors={floors} buildings={buildings}
                      onClose={() => setEditRoom(null)} onDone={loadAll}
                      openTransfer={editRoom?.__openTransfer} />
      <SetRoomFeeDialog room={setFeeRoom} onClose={() => setSetFeeRoom(null)} onDone={loadAll} />
      <EditBedDialog bed={editBed} rooms={rooms} floors={floors} onClose={() => setEditBed(null)} onDone={loadAll} />
      <GenerateBedsDialog room={generateFor} onClose={() => setGenerateFor(null)} onDone={loadAll} />

      <AssignBedDialog
        bed={assignBed} query={assignQuery} setQuery={setAssignQuery}
        results={assignResults} onCancel={() => { setAssignBed(null); setAssignQuery(""); }}
        onPick={doAssign}
      />
      <ViewResidentsDialog room={viewResidentsOf} beds={viewResidentsOf ? (bedsByRoom[viewResidentsOf.id] || []) : []}
        residentById={residentById} onClose={() => setViewResidentsOf(null)} />

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-ok"
              className={confirm?.variant === "destructive"
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
              onClick={async () => { const fn = confirm.onConfirm; setConfirm(null); if (fn) await fn(); }}
            >Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* =========================================================
 *  Room Card
 * =======================================================*/
function RoomCard({
  room, floorName, beds, residentById, canEdit, bedStatusFilter, searchHighlight,
  onEditRoom, onDeleteRoom, onTransferRoom, onGenerateBeds, onViewResidents,
  onSetFee,
  onEditBed, onDeleteBed, onAssign, onVacate, onStatus,
}) {
  const st = roomStats(beds);
  const shown = bedStatusFilter === "all" ? beds : beds.filter((b) => b.status === bedStatusFilter);

  return (
    <DroppableRoom roomId={room.id}>
      <DraggableRoomCard roomId={room.id} canDrag={canEdit}>
        <Card className="p-4 border-border bg-white space-y-3" data-testid={`room-${room.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <DoorOpen className="w-4 h-4 text-primary shrink-0" />
                <div className="font-display font-bold text-lg truncate">Room {room.room_no}</div>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {floorName} · Capacity {room.capacity || st.total}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Badge variant="secondary" className="text-xs font-mono">{st.occupied}/{st.total}</Badge>
              {canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                      data-testid={`room-menu-${room.id}`}>
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Room {room.room_no}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onEditRoom}>
                      <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onSetFee} data-testid={`room-set-fee-${room.id}`}>
                      <Wallet className="w-3.5 h-3.5 mr-2" /> Set Monthly Fee
                      {room.monthly_fee ? (
                        <span className="ml-auto text-xs font-mono text-muted-foreground">
                          ₹{Number(room.monthly_fee).toLocaleString("en-IN")}
                        </span>
                      ) : null}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onTransferRoom}>
                      <ArrowRightLeft className="w-3.5 h-3.5 mr-2" /> Move to another floor
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onGenerateBeds}>
                      <Sparkles className="w-3.5 h-3.5 mr-2" /> Generate beds
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onViewResidents}>
                      <User className="w-3.5 h-3.5 mr-2" /> View residents
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onDeleteRoom} className="text-destructive">
                      <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* stats bar */}
          <div>
            <div className="flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
              <span>{st.pct}% occupied</span>
              <span>Vacant {st.vacant}</span>
            </div>
            <Progress value={st.pct} className="h-1.5" />
          </div>
          <div className="grid grid-cols-4 gap-1 text-[10px]">
            <StatPill dot="bg-emerald-500" label="Free" value={st.vacant} />
            <StatPill dot="bg-primary"     label="Occ"  value={st.occupied} />
            <StatPill dot="bg-amber-500"   label="Res"  value={st.reserved} />
            <StatPill dot="bg-slate-400"   label="Mnt/Blk" value={st.maintenance + st.blocked} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {shown.length === 0 && (
              <div className="col-span-full text-xs text-muted-foreground italic py-2">
                {beds.length === 0 ? "No beds yet." : "No beds match filter."}
              </div>
            )}
            {shown.map((bed) => (
              <BedTile
                key={bed.id}
                bed={bed}
                canEdit={canEdit}
                resident={bed.resident_id ? residentById[bed.resident_id] : null}
                highlight={searchHighlight}
                onEdit={() => onEditBed(bed)}
                onDelete={() => onDeleteBed(bed)}
                onAssign={() => onAssign(bed)}
                onVacate={() => onVacate(bed)}
                onStatus={(s) => onStatus(bed, s)}
              />
            ))}
          </div>
        </Card>
      </DraggableRoomCard>
    </DroppableRoom>
  );
}

function StatPill({ dot, label, value }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-secondary/50">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      <span className="text-muted-foreground">{label}</span>
      <span className="ml-auto font-mono font-medium">{value}</span>
    </div>
  );
}

/* =========================================================
 *  Bed Tile — click / context-menu / dropdown / drag
 * =======================================================*/
function BedTile({ bed, resident, canEdit, highlight, onEdit, onDelete, onAssign, onVacate, onStatus }) {
  const isOcc = bed.status === "occupied";
  const cls = STATUS_COLORS[bed.status] || STATUS_COLORS.available;
  const nameMatch = highlight && resident?.name?.toLowerCase().includes(highlight);
  const bedMatch  = highlight && bed.bed_no?.toLowerCase().includes(highlight);

  const tile = (
    <div
      className={`relative rounded-md border px-2 py-2 transition-all ${cls} ${
        nameMatch || bedMatch ? "ring-2 ring-primary" : ""
      } ${canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-default"}`}
      data-testid={`bed-${bed.id}`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono text-[10px] font-medium truncate">{bed.bed_no}</span>
        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[bed.status]}`} />
      </div>
      <div className="text-[11px] font-medium mt-0.5 truncate">
        {isOcc ? (resident?.name || "Assigned") :
         bed.status === "available" ? "Vacant" :
         bed.status.charAt(0).toUpperCase() + bed.status.slice(1)}
      </div>
    </div>
  );

  const menu = (
    <>
      <DropdownMenuLabel>Bed {bed.bed_no}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      {isOcc ? (
        <DropdownMenuItem onClick={onVacate}>
          <LogOut className="w-3.5 h-3.5 mr-2" /> Vacate
        </DropdownMenuItem>
      ) : (
        <DropdownMenuItem onClick={onAssign} disabled={["maintenance","blocked","reserved"].includes(bed.status)}>
          <UserPlus className="w-3.5 h-3.5 mr-2" /> Assign resident
        </DropdownMenuItem>
      )}
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <ShieldAlert className="w-3.5 h-3.5 mr-2" /> Change status
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent>
            {["available", "reserved", "maintenance", "blocked"].map((s) => (
              <DropdownMenuItem key={s} onClick={() => onStatus(s)} disabled={s === bed.status || (s === "available" && isOcc)}>
                <span className={`w-2 h-2 rounded-full mr-2 ${STATUS_DOT[s]}`} />
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={onEdit}>
        <Pencil className="w-3.5 h-3.5 mr-2" /> Rename / Move
      </DropdownMenuItem>
      <DropdownMenuItem onClick={onDelete} className="text-destructive" disabled={isOcc}>
        <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
      </DropdownMenuItem>
    </>
  );

  // Only admin/warden get the interactive menus; others just see the tile
  if (!canEdit) return tile;

  return (
    <DraggableBed bed={bed} canDrag={true}>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full text-left" data-testid={`bed-btn-${bed.id}`}>{tile}</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">{menu}</DropdownMenuContent>
            </DropdownMenu>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-56">
          <ContextMenuItem onClick={onEdit}><Pencil className="w-3.5 h-3.5 mr-2" /> Rename / Move</ContextMenuItem>
          {isOcc ? (
            <ContextMenuItem onClick={onVacate}><LogOut className="w-3.5 h-3.5 mr-2" /> Vacate</ContextMenuItem>
          ) : (
            <ContextMenuItem onClick={onAssign}><UserPlus className="w-3.5 h-3.5 mr-2" /> Assign resident</ContextMenuItem>
          )}
          <ContextMenuSub>
            <ContextMenuSubTrigger><ShieldAlert className="w-3.5 h-3.5 mr-2" /> Change status</ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {["available", "reserved", "maintenance", "blocked"].map((s) => (
                <ContextMenuItem key={s} onClick={() => onStatus(s)} disabled={s === bed.status || (s === "available" && isOcc)}>
                  <span className={`w-2 h-2 rounded-full mr-2 ${STATUS_DOT[s]}`} />
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={onDelete} className="text-destructive" disabled={isOcc}>
            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </DraggableBed>
  );
}

/* =========================================================
 *  Dialogs — small self-contained components
 * =======================================================*/
function AddBuildingDialog({ open, onOpenChange, onDone, setBId }) {
  const [f, setF] = useState({ name: "", address: "", gender: "co-ed" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ name: "", address: "", gender: "co-ed" }); }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/buildings", f);
      toast.success("Building created");
      onOpenChange(false);
      setBId?.(data.id);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-2xl">New Building</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input required value={f.name} onChange={(e)=>setF({...f, name:e.target.value})}
              data-testid="building-name" />
          </div>
          <div className="space-y-2">
            <Label>Address</Label>
            <Input value={f.address} onChange={(e)=>setF({...f, address:e.target.value})} />
          </div>
          <div className="space-y-2">
            <Label>Gender</Label>
            <Select value={f.gender} onValueChange={(v)=>setF({...f, gender:v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="co-ed">Co-ed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="building-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddFloorDialog({ open, onOpenChange, bId, floors, onDone }) {
  const [f, setF] = useState({ name: "", floor_number: 1, monthly_fee: "" });
  useEffect(() => {
    if (open) setF({ name: "", floor_number: (floors[floors.length-1]?.floor_number || 0) + 1, monthly_fee: "" });
  }, [open, floors]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/floors", { ...f, building_id: bId, floor_number: Number(f.floor_number),
        monthly_fee: f.monthly_fee === "" ? null : Number(f.monthly_fee) });
      toast.success("Floor added"); onOpenChange(false); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-2xl">New Floor</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input required value={f.name} onChange={(e)=>setF({...f, name:e.target.value})}
                placeholder="e.g. Ground, First" data-testid="floor-name" />
            </div>
            <div className="space-y-2">
              <Label>Floor No.</Label>
              <Input required type="number" value={f.floor_number}
                onChange={(e)=>setF({...f, floor_number:e.target.value})} data-testid="floor-number" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Monthly Fee (₹)</Label>
              <Input type="number" value={f.monthly_fee}
                placeholder="e.g. 2500 · auto-fills New Resident hostel fee"
                onChange={(e)=>setF({...f, monthly_fee:e.target.value})} data-testid="floor-monthly-fee" />
              <p className="text-[11px] text-muted-foreground">
                Standard tier: Underground Hall ₹1,500 · Underground Room ₹2,000 · Ground ₹2,200 · Others ₹2,500
              </p>
            </div>
          </div>
          <DialogFooter><Button type="submit" data-testid="floor-save">Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddRoomDialog({ open, onOpenChange, fId, onDone }) {
  const [f, setF] = useState({ room_no: "", capacity: 2 });
  useEffect(() => { if (open) setF({ room_no: "", capacity: 2 }); }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rooms", { ...f, floor_id: fId, capacity: Number(f.capacity) });
      toast.success(`Room + ${f.capacity} bed(s) added`);
      onOpenChange(false); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-2xl">New Room</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Room No *</Label>
              <Input required value={f.room_no} onChange={(e)=>setF({...f, room_no:e.target.value})}
                data-testid="room-no" />
            </div>
            <div className="space-y-2">
              <Label>Bed Capacity *</Label>
              <Input required type="number" min={1} value={f.capacity}
                onChange={(e)=>setF({...f, capacity:e.target.value})} data-testid="room-capacity" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {f.capacity} bed{f.capacity > 1 ? "s" : ""} will be created automatically. No upper limit.
          </p>
          <DialogFooter><Button type="submit" data-testid="room-save">Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditBuildingDialog({ building, onClose, onDone }) {
  const [f, setF] = useState({ name: "", address: "", gender: "co-ed" });
  useEffect(() => {
    if (building) setF({ name: building.name || "", address: building.address || "", gender: building.gender || "co-ed" });
  }, [building]);
  if (!building) return null;
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/buildings/${building.id}`, f);
      toast.success("Building updated"); onClose(); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-2xl">Edit Building</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input required value={f.name} onChange={(e)=>setF({...f, name:e.target.value})}
              data-testid="edit-building-name" />
          </div>
          <div className="space-y-2">
            <Label>Address</Label>
            <Input value={f.address} onChange={(e)=>setF({...f, address:e.target.value})} />
          </div>
          <div className="space-y-2">
            <Label>Gender</Label>
            <Select value={f.gender} onValueChange={(v)=>setF({...f, gender:v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="co-ed">Co-ed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter><Button type="submit" data-testid="edit-building-save">Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditFloorDialog({ floor, onClose, onDone }) {
  const [f, setF] = useState({ name: "", floor_number: 1, monthly_fee: "" });
  useEffect(() => {
    if (floor) setF({ name: floor.name || "", floor_number: floor.floor_number || 1,
                       monthly_fee: floor.monthly_fee ?? "" });
  }, [floor]);
  if (!floor) return null;
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/floors/${floor.id}`, { name: f.name, floor_number: Number(f.floor_number),
        monthly_fee: f.monthly_fee === "" ? null : Number(f.monthly_fee) });
      toast.success("Floor updated"); onClose(); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-2xl">Edit Floor</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input required value={f.name} onChange={(e)=>setF({...f, name:e.target.value})}
                data-testid="edit-floor-name" />
            </div>
            <div className="space-y-2">
              <Label>Floor No.</Label>
              <Input required type="number" value={f.floor_number}
                onChange={(e)=>setF({...f, floor_number:e.target.value})} data-testid="edit-floor-number" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Monthly Fee (₹)</Label>
              <Input type="number" value={f.monthly_fee}
                placeholder="Leave blank to unset"
                onChange={(e)=>setF({...f, monthly_fee:e.target.value})} data-testid="edit-floor-monthly-fee" />
              <p className="text-[11px] text-muted-foreground">
                Auto-fills the Hostel Fee input on the New Resident dialog when a bed on this floor is selected.
              </p>
            </div>
          </div>
          <DialogFooter><Button type="submit" data-testid="edit-floor-save">Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditRoomDialog({ room, floors, buildings, onClose, onDone, openTransfer }) {
  const [f, setF] = useState({ room_no: "", capacity: 1, gender: "", floor_id: "" });
  const [warn, setWarn] = useState(false);
  useEffect(() => {
    if (room) setF({
      room_no: room.room_no || "",
      capacity: room.capacity || 1,
      gender: room.gender || "",
      floor_id: room.floor_id || "",
    });
  }, [room]);
  if (!room) return null;

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      room_no: f.room_no,
      capacity: Number(f.capacity),
      gender: f.gender || null,
      floor_id: f.floor_id,
    };
    try {
      await api.put(`/rooms/${room.id}`, payload);
      toast.success("Room updated"); onClose(); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const buildingFor = (fl) => buildings.find((b)=>b.id===fl.building_id)?.name || "";
  const floorChanged = f.floor_id !== room.floor_id;

  return (
    <Dialog open onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit Room</DialogTitle>
          <DialogDescription>Rename, resize, or move this room and all its beds.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e)=>{
            e.preventDefault();
            const hasOccupied = false; // (info-only; server enforces)
            if (floorChanged) { setWarn(true); return; }
            submit(e);
          }} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Room No *</Label>
              <Input required value={f.room_no} onChange={(e)=>setF({...f, room_no:e.target.value})}
                data-testid="edit-room-no" />
            </div>
            <div className="space-y-2">
              <Label>Capacity</Label>
              <Input type="number" min={1} value={f.capacity}
                onChange={(e)=>setF({...f, capacity:e.target.value})} data-testid="edit-room-capacity" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Move to floor</Label>
            <Select value={f.floor_id} onValueChange={(v)=>setF({...f, floor_id:v})}>
              <SelectTrigger data-testid="edit-room-floor"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {floors.map((fl) => (
                  <SelectItem key={fl.id} value={fl.id}>
                    {buildingFor(fl)} · {fl.name} (F{fl.floor_number})
                    {fl.id === room.floor_id ? "  · current" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Gender restriction</Label>
            <Select value={f.gender || "none"} onValueChange={(v)=>setF({...f, gender: v==="none"?"":v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter><Button type="submit" data-testid="edit-room-save">Save</Button></DialogFooter>
        </form>

        <AlertDialog open={warn} onOpenChange={setWarn}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Move Room {room.room_no}?</AlertDialogTitle>
              <AlertDialogDescription>
                All beds inside this room (including any occupied residents) will move with it. Continue?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { setWarn(false); submit(e); }}>Confirm move</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

function EditBedDialog({ bed, rooms, floors, onClose, onDone }) {
  const [f, setF] = useState({ bed_no: "", target_room_id: "" });
  useEffect(() => { if (bed) setF({ bed_no: bed.bed_no, target_room_id: bed.room_id }); }, [bed]);
  if (!bed) return null;

  const submit = async (e) => {
    e.preventDefault();
    try {
      // If room changed → transfer, else just rename
      if (f.target_room_id && f.target_room_id !== bed.room_id) {
        await api.post(`/beds/${bed.id}/transfer`, { target_room_id: f.target_room_id, new_bed_no: f.bed_no });
        toast.success("Bed moved");
      } else if (f.bed_no !== bed.bed_no) {
        await api.put(`/beds/${bed.id}`, { bed_no: f.bed_no });
        toast.success("Bed renamed");
      } else {
        toast.info("Nothing changed");
      }
      onClose(); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const floorName = (fid) => floors.find((f) => f.id === fid)?.name || "";

  return (
    <Dialog open onOpenChange={(v)=>!v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit Bed</DialogTitle>
          <DialogDescription>Rename this bed or move it to another room.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Bed Number *</Label>
            <Input required value={f.bed_no} onChange={(e)=>setF({...f, bed_no:e.target.value})}
              data-testid="edit-bed-no" />
          </div>
          <div className="space-y-2">
            <Label>Room</Label>
            <Select value={f.target_room_id} onValueChange={(v)=>setF({...f, target_room_id:v})}>
              <SelectTrigger data-testid="edit-bed-room"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {rooms.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    Room {r.room_no} · {floorName(r.floor_id)}
                    {r.id === bed.room_id ? "  · current" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {bed.resident_id && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded">
              This bed is occupied — moving it will carry the resident along.
            </div>
          )}
          <DialogFooter><Button type="submit" data-testid="edit-bed-save">Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GenerateBedsDialog({ room, onClose, onDone }) {
  const [f, setF] = useState({ count: 10, start: 1, prefix: "" });
  useEffect(() => {
    if (room) setF({ count: 10, start: 1, prefix: `${room.room_no}-` });
  }, [room]);

  const preview = useMemo(() => {
    const list = [];
    const cnt = Math.min(Number(f.count) || 0, 6);
    for (let i = 0; i < cnt; i++) list.push(`${f.prefix}${Number(f.start) + i}`);
    if ((Number(f.count) || 0) > cnt) list.push("…", `${f.prefix}${Number(f.start) + Number(f.count) - 1}`);
    return list;
  }, [f]);

  if (!room) return null;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/rooms/${room.id}/beds/generate`, {
        count: Number(f.count), start: Number(f.start), prefix: f.prefix,
      });
      toast.success(`Created ${data.created.length} bed(s)${data.skipped.length ? `, skipped ${data.skipped.length} duplicate(s)` : ""}`);
      onClose(); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open onOpenChange={(v)=>!v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Generate Beds — Room {room.room_no}</DialogTitle>
          <DialogDescription>Bulk create beds. No upper limit.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Count *</Label>
              <Input required type="number" min={1} value={f.count}
                onChange={(e)=>setF({...f, count:e.target.value})} data-testid="gen-count" />
            </div>
            <div className="space-y-2">
              <Label>Start #</Label>
              <Input type="number" value={f.start}
                onChange={(e)=>setF({...f, start:e.target.value})} data-testid="gen-start" />
            </div>
            <div className="space-y-2">
              <Label>Prefix</Label>
              <Input value={f.prefix} onChange={(e)=>setF({...f, prefix:e.target.value})}
                data-testid="gen-prefix" />
            </div>
          </div>
          <div className="bg-secondary/60 border border-border rounded-md p-3 text-xs">
            <div className="text-muted-foreground mb-1">Preview:</div>
            <div className="font-mono flex flex-wrap gap-1">
              {preview.map((p, i) => (
                <span key={`prev-${i}-${p}`} className="px-1.5 py-0.5 bg-white rounded border border-border">{p}</span>
              ))}
            </div>
          </div>
          <DialogFooter><Button type="submit" data-testid="gen-save">Generate</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AssignBedDialog({ bed, query, setQuery, results, onCancel, onPick }) {
  return (
    <Dialog open={!!bed} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Assign bed {bed?.bed_no}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input autoFocus placeholder="Search by name, phone, student ID…"
            value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid="assign-search" />
          <div className="max-h-64 overflow-y-auto space-y-1 border border-border rounded-md">
            {results.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground text-center">
                {query ? "No matches." : "Start typing to search residents."}
              </div>
            )}
            {results.map((r) => (
              <button key={r.id} onClick={() => onPick(r)}
                className="w-full text-left px-3 py-2 hover:bg-secondary transition-colors"
                data-testid={`assign-result-${r.id}`}>
                <div className="font-medium text-sm">{r.name}</div>
                <div className="text-xs text-muted-foreground">
                  {[r.phone, r.company].filter(Boolean).join(" • ") || "—"}
                  {r.room_no && ` • currently in ${r.room_no}`}
                </div>
              </button>
            ))}
          </div>
          <Button variant="ghost" onClick={onCancel}>
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ViewResidentsDialog({ room, beds, residentById, onClose }) {
  if (!room) return null;
  const occ = beds.filter((b) => b.resident_id);
  return (
    <Dialog open onOpenChange={(v)=>!v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Residents in Room {room.room_no}</DialogTitle>
          <DialogDescription>{occ.length} occupied bed(s)</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto divide-y">
          {occ.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">No residents assigned yet.</div>
          )}
          {occ.map((b) => {
            const r = residentById[b.resident_id];
            return (
              <div key={b.id} className="py-2 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{r?.name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{r?.phone || "—"}</div>
                </div>
                <Badge variant="secondary" className="text-xs font-mono">{b.bed_no}</Badge>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== v34 · Set Monthly Fee Dialog ==================== */
function SetRoomFeeDialog({ room, onClose, onDone }) {
  const [fee, setFee] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (room) setFee(room.monthly_fee != null ? String(room.monthly_fee) : "");
  }, [room]);
  if (!room) return null;
  const submit = async (e) => {
    e.preventDefault();
    const val = fee === "" ? null : Number(fee);
    if (val !== null && (!Number.isFinite(val) || val < 0)) {
      toast.error("Fee must be a non-negative number"); return;
    }
    setSaving(true);
    try {
      await api.put(`/rooms/${room.id}`, { monthly_fee: val });
      toast.success(val === null ? "Room fee cleared" : `Room fee set to ₹${val.toLocaleString("en-IN")}`);
      onClose(); onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="set-room-fee-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Set Monthly Fee</DialogTitle>
          <DialogDescription>
            Room {room.room_no}. This fee auto-applies to every resident allocated
            to a bed in this room (cascade: Bed → Room → Floor → Agreed Fee), with
            authorised discounts subtracted afterwards.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Monthly Fee (₹)</Label>
            <Input type="number" min="0" step="1" value={fee}
              onChange={(e) => setFee(e.target.value)}
              placeholder="e.g. 5000  ·  leave blank to inherit Floor rate"
              data-testid="set-room-fee-input" autoFocus />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="set-room-fee-save">
              {saving ? "Saving…" : "Save Fee"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

